<template>
  <div class="product-card-type-f">
    <a
      :href="productUrl"
      class="product-card-type-f__img-wrapper pc__images"
      :aria-label="`View ${product.title}`"
    >
      <ProductImage
        :enableImageCarousel="false"
        :selectedColor="selectedColor"
        :product="product.product"
      />

      <div v-if="averageRating > 0" class="product-card-type-f__product-rating">
        <svg aria-hidden="true">
          <use xlink:href="#rating-star"></use>
        </svg>

        <strong>{{ averageRating }}</strong>
      </div>
    </a>

    <div class="product-card-type-f__details-wrapper">
      <Swatch
        v-if="hasMultipleColors"
        :colorOptions="colorOptions"
        :selectedColor="selectedColor"
        :windowWidth="windowWidth"
        @color-change="updateSelectedColor"
      />

      <span class="product-card-type-f__product-title" role="heading" aria-level="2">
        {{ product.title }}
      </span>

      <div class="product-card-type-f__price-wrapper">
        <span :class="['product-card-type-f__price', { bold: comparePrice != discountPrice }]">
          {{ formatPrice(discountPrice) }}
        </span>
        <span v-if="comparePrice != discountPrice" class="product-card-type-f__compare-price">
          {{ formatPrice(comparePrice) }}
        </span>
      </div>
    </div>

    <button class="btn btn--outline-accent product-card-type-f__cta" @click="toggleQuickView" data-ctl-click="true">
      Quick View
    </button>
  </div>
</template>

<script setup>
  /** ProductCardTypeF is used in New Complete the look section in PDP */
  import { toRefs, computed, ref, watch } from 'vue';
  import productCardProps from './product-card-props';
  import ProductImage from './product-image.vue';
  import Swatch from './product-swatch.vue';
  import useWindowResize from '../../../utils/use-window-resize';

  const props = defineProps({
    product: productCardProps.product,
  });

  const { product } = toRefs(props);
  const { windowWidth } = useWindowResize();
  const selectedColor = ref(product.value.selectedFirstOption);

  // Keep card swatch + QV color in sync when CTL list refreshes after a parent color change
  watch(
    () => product.value.selectedFirstOption,
    (color) => {
      if (color) selectedColor.value = color;
    }
  );

  const colorOptions = computed(() => {
    const { variants, images, available } = product.value.product;
    if (!variants) return {};

    const firstLevelKeys = variants.reduce((set, variant) => {
      const property = variant.option1;
      const setItem = (set[property] = set[property] || { items: [] });
      setItem.items.push(variant);
      if (variant.available) setItem.items.available = true;
      return set;
    }, {});

    Object.entries(firstLevelKeys).forEach(([key, value]) => {
      const isAvailable = value.items.available;

      if (isAvailable || !available) {
        const imagePattern = new RegExp(`^\\s*${key.toLowerCase()}\\s*swatch\\s*$`);
        value.image = images?.filter((image) => {
          const alt = (image.alt || '').toLowerCase();
          return alt && imagePattern.test(alt);
        })[0];

        value.price = Number(value.items[0].price);
        value.comparePrice = Number(value.items[0].compare_at_price);
      } else {
        delete firstLevelKeys[key];
      }
    });

    return firstLevelKeys;
  });

  const hasMultipleColors = computed(() => Object.keys(colorOptions.value).length > 1);

  const activeColorData = computed(() => {
    return selectedColor.value && colorOptions.value[selectedColor.value];
  });

  const discountPrice = computed(() => {
    const customerDiscountVal = window.customerDiscount(product.value.product.tags);

    if (activeColorData.value) {
      const { price, comparePrice } = activeColorData.value;
      if (comparePrice > 0) {
        return Math.min(comparePrice * (1 - customerDiscountVal), price);
      }
      return price * (1 - customerDiscountVal);
    }

    const { price, compare_at_price } = product.value;
    if (compare_at_price > 0) {
      return Math.min(compare_at_price * (1 - customerDiscountVal), price);
    }
    return price * (1 - customerDiscountVal);
  });

  const comparePrice = computed(() => {
    if (activeColorData.value) {
      return activeColorData.value.comparePrice > 0
        ? activeColorData.value.comparePrice
        : activeColorData.value.price;
    }

    const { price, compare_at_price } = product.value;
    return compare_at_price > 0 ? compare_at_price : price;
  });

  const averageRating = computed(() =>  product?.value.product?.review_rating?.reviews_average || 0);

  const productUrl = computed(() => {
    const handle = product.value.product?.handle;

    if (!selectedColor.value) {
      return `/products/${handle}`;
    }

    return `/products/${handle}?color=${encodeURIComponent(selectedColor.value)}`;
  });

  const updateSelectedColor = (color) => {
    selectedColor.value = color;
  };

  const formatPrice = (price) => {
    return window.formattedPrice(price / 100);
  };

  const toggleQuickView = () => {
    window.toggleDrawer(true, 'QuickView', {
      product: product.value.product,
      selectedColor: selectedColor.value || product.value.selectedFirstOption,
      expandProductDetailsInPlace: true,
    });
  };
</script>
